import React, { useMemo } from 'react'
import classNames from 'classnames'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../SelectDropdown/SelectDropdown'
import { TIMEZONE_LABELS } from './constants'
import { getGmtOffsetLabel } from './utils'

interface TimezoneSelectorProps {
  initialSelectedTimezone: string
  onTimezoneSelected: (timezone: string | null) => void
  loading?: boolean
  disabled?: boolean
}

const renderTimezoneLabel = (label: string) => {
  const match = label.match(/^(.*?)\s*(\(.*\))\s*$/)
  if (!match) {
    return <strong>{label}</strong>
  }

  const main = match[1]
  const suffix = match[2]
  return (
    <>
      <strong>{main}</strong> {suffix}
    </>
  )
}

const renderTimezoneOptionContent = (ianaTimezone: string) => {
  const label = Object.entries(TIMEZONE_LABELS).find(([, tz]) => tz === ianaTimezone)?.[0] ?? ianaTimezone
  const gmtOffsetLabel = getGmtOffsetLabel(ianaTimezone)

  return (
    <>
      <span className='text-nowrap'>{gmtOffsetLabel}</span>
      <span className='text-truncate'>{renderTimezoneLabel(label)}</span>
    </>
  )
}

const TimezoneDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick, displayLabel, disabled, className }) => {
  const isSeparator = optionId === 'timezone-separator'

  if (isSeparator) {
    return <div className={classNames('px-0', className)}>{displayLabel ?? optionLabel}</div>
  }

  return (
    <CustomDropdownItemOption
      role='button'
      onClick={disabled ? () => {} : onClick}
      className={classNames('d-flex gap-2 align-items-center text-truncate paragraph-medium py-2', className, {
        'zocks-text-gray-500': disabled,
      })}
    >
      {displayLabel ?? optionLabel}
    </CustomDropdownItemOption>
  )
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ initialSelectedTimezone, onTimezoneSelected, loading = false, disabled = false }) => {
  const timezoneOptions = useMemo<SelectOption[]>((): SelectOption[] => {
    const entries = Object.entries(TIMEZONE_LABELS).map(([label, ianaTimezone]) => {
      const gmtOffsetLabel = getGmtOffsetLabel(ianaTimezone)
      return {
        id: ianaTimezone,
        label: `${gmtOffsetLabel} ${label}`,
        displayLabel: <span className='d-flex align-items-baseline gap-1 text-truncate'>{renderTimezoneOptionContent(ianaTimezone)}</span>,
      }
    })

    const isUsTimezone = (ianaTimezone: string) => {
      const usTimezones = new Set([
        'Pacific/Honolulu',
        'America/Anchorage',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Denver',
        'America/Chicago',
        'America/New_York',
        'America/Indiana/Indianapolis',
      ])

      return usTimezones.has(ianaTimezone)
    }

    const sortedUs = entries.filter((e) => isUsTimezone(e.id)).sort((a, b) => a.label.localeCompare(b.label))
    const sortedRest = entries.filter((e) => !isUsTimezone(e.id)).sort((a, b) => a.label.localeCompare(b.label))

    const separator: SelectOption = {
      id: 'timezone-separator',
      label: '──────────',
      displayLabel: <div className='w-100 border-top my-1' />,
      disabled: true,
      className: 'px-0',
    }

    return [...sortedUs, separator, ...sortedRest]
  }, [])

  const initialSelectedOption = useMemo<SelectOption | null>(
    (): SelectOption | null => timezoneOptions.find((o) => o.id === initialSelectedTimezone) ?? null,
    [initialSelectedTimezone, timezoneOptions],
  )

  const handleOptionSelected = (option: SelectOption) => {
    if (option.disabled) {
      return
    }

    onTimezoneSelected(option.id)
  }

  return (
    <div>
      <SelectDropdown
        options={timezoneOptions}
        initialSelectedOption={initialSelectedOption}
        onOptionSelected={handleOptionSelected}
        placeholder='Select Timezone'
        DropDownItemOptionComponent={TimezoneDropdownItemOption}
        loading={loading}
        disabled={disabled}
        searchable
        hideClearButton={true}
      />
    </div>
  )
}
